import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Warehouse } from "../types";
import { fetchDefaultWarehouse, fetchAllWarehouses } from "../services/warehouseService";

interface WarehouseStore {
  // Состояние
  defaultWarehouse: Warehouse | null;
  allWarehouses: Warehouse[];
  selectedWarehouse: Warehouse | null;
  isLoading: boolean;
  error: string | null;

  // Действия
  setDefaultWarehouse: (warehouse: Warehouse) => void;
  setAllWarehouses: (warehouses: Warehouse[]) => void;
  setSelectedWarehouse: (warehouse: Warehouse | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Асинхронные действия
  initializeWarehouse: () => Promise<void>;
  loadAllWarehouses: () => Promise<void>;
  selectWarehouseById: (id: number) => void;
  resetToDefault: () => void;
}

export const useWarehouseStore = create<WarehouseStore>()(
  persist(
    (set, get) => ({
      // Начальное состояние
      defaultWarehouse: null,
      allWarehouses: [],
      selectedWarehouse: null,
      isLoading: false,
      error: null,

      // Синхронные действия
      setDefaultWarehouse: (warehouse) => set({ defaultWarehouse: warehouse }),
      setAllWarehouses: (warehouses) => set({ allWarehouses: warehouses }),
      setSelectedWarehouse: (warehouse) => set({ selectedWarehouse: warehouse }),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),

      // Асинхронные действия
      initializeWarehouse: async () => {
        try {
          set({ isLoading: true, error: null });
          
          let defaultWarehouse = null as Warehouse | null;
          try {
            defaultWarehouse = await fetchDefaultWarehouse();
          } catch (_) {
            // Если эндпоинта default нет, выберем по умолчанию Нурмагамбетова из списка
            const warehouses = await fetchAllWarehouses();
            defaultWarehouse = warehouses.find(w => w.name.toLowerCase().includes('нурмагамбет')) || warehouses[0] || null;
          }

          set({
            defaultWarehouse,
            selectedWarehouse: defaultWarehouse,
            isLoading: false,
          });
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : "Ошибка инициализации склада",
            isLoading: false 
          });
        }
      },

      loadAllWarehouses: async () => {
        try {
          set({ isLoading: true, error: null });
          
          const warehouses = await fetchAllWarehouses();
          set({ allWarehouses: warehouses, isLoading: false });
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : "Ошибка загрузки складов",
            isLoading: false 
          });
        }
      },

      selectWarehouseById: (id) => {
        const { allWarehouses } = get();
        const warehouse = allWarehouses.find(w => w.id === id);
        if (warehouse) {
          set({ selectedWarehouse: warehouse });
        }
      },

      resetToDefault: () => {
        const { defaultWarehouse } = get();
        if (defaultWarehouse) {
          set({ selectedWarehouse: defaultWarehouse });
        }
      },
    }),
    {
      name: "warehouse-storage",
      partialize: (state) => ({
        defaultWarehouse: state.defaultWarehouse,
        selectedWarehouse: state.selectedWarehouse,
      }),
    }
  )
);

